(ns medplast.views.grids.statistics
  (:require
   [cljc.java-time.local-date :as local-date]
   [clojure.string]
   [medplast.firestore :as firestore]
   [medplast.lang :as lang :refer [parse-local-date]]
   [medplast.state :as state]
   [medplast.views.core :as views-core :refer [header minor-header]]
   [medplast.views.forms.core :as forms-core]
   [medplast.views.forms.sale :as forms-sale]
   [medplast.views.grids.core :as grids-core :refer [table]]
   [medplast.views.grids.sale :as grids-sale :refer [sales-table]]
   [medplast.views.grids.patient :as grids-patient
    :refer [patient-activations-by-user-in-period-table patient-deactivations-by-user-in-period-table]]
   [reagent.core :as r]))

(defn valid-sale? [sale]
  (let [stock-expiration-date (parse-local-date (:stock-expiration-date sale))
        sale-date (parse-local-date (:sale-date sale))
        valid-sale (> stock-expiration-date sale-date)]
    valid-sale))

(defn project-sale [sale]
  ; given a sale, return another sale that has same duration and starts when
  ; the first one ended
  (let [stock-expiration-date (parse-local-date (:stock-expiration-date sale))
        projected-sale (forms-sale/get-initial-replacement-sale-from-old-sale
                        sale {:new-sale-date stock-expiration-date})]
    projected-sale))

(defn get-product-id-to-quantity-map-from-sales [sales]
  ; flattens the sales sequence, so [[1],[1]] == [1,1]
  (let [reduce-sales-to-stock-requirements
        (fn [stocks sale]
          (let [stock-with-ids-dekeywordized (-> sale :stock (update-keys name))]
            (merge-with + stocks stock-with-ids-dekeywordized)))
        product-id-to-quantity-requirements
        (->>
         sales
         flatten
         (reduce reduce-sales-to-stock-requirements {}))]
    product-id-to-quantity-requirements))

; NOTE, inf-sales means the sequence might be infinite, don't materialize
(defn slice-sale-period-from-chronological-sales [period inf-sales]
  ; slices out sales that were performed during the period
  ; sales have to be in sale-date chronological order
  (let [sale-sold-before-start-of-period
        (fn [sale]
          (< (parse-local-date (:sale-date sale))
             (parse-local-date (:start-date period))))
        sale-sold-before-end-of-period
        (fn [sale]
          (<= (parse-local-date (:sale-date sale))
              (parse-local-date (:end-date period))))]
    (->>
     inf-sales
     ; drop sales while expiration date (not sale date) before period start
     (drop-while sale-sold-before-start-of-period)
     ; take sales while sale date before-or-at period end
     (take-while sale-sold-before-end-of-period))))

; NOTE, inf-sales means the sequence might be infinite, don't materialize
#_(defn slice-expiration-period-from-chronological-sales [period inf-sales]
  ; slices out sales that expire during the period
  ; sales have to be in chronological order
    (let [sale-expires-before-start-of-period
          (fn [sale]
            (< (parse-local-date (:stock-expiration-date sale))
               (:start-date period)))
          sale-expires-before-end-of-period
          (fn [sale]
            (<= (parse-local-date (:stock-expiration-date sale))
                (:end-date period)))]
      (->>
       inf-sales
     ; drop sales while expiration date (not sale date) before period start
       (drop-while sale-expires-before-start-of-period)
     ; take sales while expiration date before-or-at period end
       (take-while sale-expires-before-end-of-period))))

(defn get-projected-sales-atom [period user-uid]
  (let [active-sales-atom
        (r/reaction
         (if user-uid
           @@(r/track state/get-user-active-sales-atom user-uid)
           @@(r/track state/get-all-active-sales-atom)))
        projected-sales-atom
        (r/reaction
         (when-let [active-sales @active-sales-atom]
           (->>
            active-sales
            ; filter out invalid sales
            (filter valid-sale?)
            ; perform projection for each sale
            (map #(iterate project-sale %))
            ; remove first sale for each sale chain.
            ; that's the non-fictive (currently active) sale
            (map rest)
            (map (partial slice-sale-period-from-chronological-sales period))
            flatten)))]
    projected-sales-atom))

(defn get-past-sales-atom [period user-uid]
  (let [sales-in-period-atom
        (r/reaction
         (if user-uid
           @@(r/track state/get-user-valid-sold-sales-in-period-sale-ordered-atom period user-uid)
           @@(r/track state/get-all-valid-sales-in-period-sale-ordered-atom period)))
        ;; sales-in-period-atom
        ;; (r/reaction
        ;;  (slice-sale-period-from-chronological-sales
        ;;   period @sales-expiration-ordered-atom))
        ]
    sales-in-period-atom))

(def projected-stock-requirements-table-col-defs
  [{:field "type" :headerName "Rūšis" :width 150
    :autoHeight true
    :wrapText true}
   {:field "name" :headerName "Pavadinimas"
    :flex 0.15 :minWidth 150
    :maxWidth 250
    :autoHeight true :wrapText true}
   {:field "variant-name" :headerName "Varianto pavad."
    :flex 0.3 :minWidth 250
    :maxWidth 350
    :autoHeight true
    :wrapText true
    :autoHeaderHeight true
    :wrapHeaderText true}
   ;;  {:field "quantity-sold" :headerName "Kiekis parduotas"
   ;;   :width 120
   ;;   :filter true
   ;;   :autoHeaderHeight true
   ;;   :wrapHeaderText true}
   {:field "quantity-projected" :headerName "Kiekis kurį tikimasi parduoti"
    :width 120
    :filter true
    :autoHeaderHeight true
    :wrapHeaderText true}])

(def projected-stock-requirements-table-initial-column-config
  [{:colId "type", :sort "asc", :sortIndex 0}
   {:colId "name", :sort "asc", :sortIndex 1}
   {:colId "variant-name", :sort "asc", :sortIndex 2}])

(defn projected-stock-requirements-table [period-atom user-uid]
  (let [get-product-id-to-keyed-quantity-map
        (fn [key sales]
          (when sales
            (->
             (get-product-id-to-quantity-map-from-sales sales)
             (update-vals (fn [quantity] (assoc {} key quantity))))))
        product-id-to-projected-quantity-column-atom
        (r/reaction
         (get-product-id-to-keyed-quantity-map
          :quantity-projected
          @@(r/track get-projected-sales-atom @period-atom user-uid)))
        row-data-atom
        (r/reaction
         (when-let [product-id-to-columns @product-id-to-projected-quantity-column-atom]
           (when-let [id-to-product-map @@(r/track state/get-id-to-product-map-atom)]
             (->>
              (update-keys
               product-id-to-columns
               id-to-product-map)
              (map
               (fn [[{:keys [name variant-name type] :as product}
                     {:keys [quantity-projected] :as _column}]]
                 {:name name
                  :variant-name variant-name
                  :type type
                  :quantity-projected quantity-projected
                  :id (firestore/get-doc-id product)}))))))]
    [table {:row-data-atom row-data-atom
            :col-defs projected-stock-requirements-table-col-defs
            :initial-column-config projected-stock-requirements-table-initial-column-config}]))

(defn past-sales-table [period-atom user-uid]
  (let [past-sales-atom (r/reaction
                         @@(r/track get-past-sales-atom @period-atom user-uid))
        user-atom (r/reaction
                   (if user-uid
                     @@(r/track state/get-user-atom user-uid)
                     @@(r/track state/get-logged-in-user-atom)))]
    [sales-table user-atom past-sales-atom]))

(defn period-from-match [match]
  (let [{:keys [start-date end-date]} (:query-params match)]
    (when (and start-date end-date)
      (lang/make-period start-date end-date))))

(defn make-period-watcher [match]
  ; Creates a period atom, initialized from query params, and adds a watch that updates query params on change.
  ; Returns a map with the atom and a function to remove the watch.
  (let [initial-period
        (or (period-from-match match)
            (lang/make-period (str (local-date/now)) (str (local-date/now))))
        period-atom (r/atom initial-period)
        _ (add-watch
           period-atom :period-watch
           (fn [_ _ _old-period new-period]
             (state/replace-query-params!
              {:start-date (:start-date new-period)
               :end-date (:end-date new-period)})))
        remove-period-watch (fn [] (remove-watch period-atom :period-watch))]
    {:period-atom period-atom
     :remove-period-watch remove-period-watch}))

(defn make-statistics-page [table-component heading explanation]
  ; Takes a table component and returns a page that displays it for all users and for each user separately.
  ; Includes period selector, whose state is passed to the mentioned component.
  (fn [match]
    (r/with-let [{:keys [period-atom remove-period-watch]} (make-period-watcher match)]
      (let [active-users-atom @(r/track state/get-active-users-atom)]
        [:<>
         [header heading
          {:on-click views-core/go-back! :text "Atgal"}]
         [:p {:class "max-w-3xl"} explanation]
         [minor-header "Laikotarpio pasirinkimas"]
         [forms-core/period-selector period-atom]
         (if (lang/valid-period? @period-atom)
           [:<>
            [minor-header "Visi vartotojai"]
            [table-component period-atom nil]
          ; [patient-activation-per-user period-atom]
            [minor-header "Aktyvūs vartotojai atskirai"]
            (if-let [active-users @active-users-atom]
              (for [user active-users]
                (let [user-uid (firestore/get-doc-id user)]
                  ^{:key user-uid} [:<>
                                    [:div
                                     {:class "-mb-3"}
                                     [views-core/href-link
                                      (views-core/get-href-for-user user)
                                      (views-core/rich-user-label user)]]
                                    [table-component period-atom user-uid]]))
              "Kraunama...")]
           [:span "Pradžios data turi būti anksčiau negu (arba ta pati kaip) pabaigos data."])])
      (finally
        (remove-period-watch)))))

(def projected-stock-requirements-page
  (make-statistics-page projected-stock-requirements-table
                        "Prekių poreikio projekcija"
                        "Projekcija prekių, kurių tikimasi parduoti per pasirinktą laikotarpį. Atsižvelgiama tik į aktyvius pardavimus. Daroma prielaida, kad pasibaigus jų galiojimui, visi pardavimai bus atnaujinti išlaikant tuos pačius produktus, kiekius ir galiojimo terminą, o vėliau šie atnaujinti pardavimai bus toliau periodiškai atnaujinami tokiu pačiu principu."))

(def past-sales-page
  (make-statistics-page past-sales-table
                        "Pardavimų sukūrimo statistika"
                        "Pardavimų įrašai, kurie nėra pažymėti kaip klaidingi ir kurių pardavimo data patenka į pasirinktą laikotarpį. Pardavimai grupuojami pagal vartotoja, kuris juos sukūrė (ne pagal priskirtą vartotoją)."))

(def patient-activations-page
  (make-statistics-page patient-activations-by-user-in-period-table
                        "Pacientų sukūrimo statistika"
                        "Pacientų įrašai, kurie nėra pažymėti kaip klaidingi ir kurių sukūrimo data patenka į pasirinktą laikotarpį. Pacientai grupuojami pagal vartotoją, kuris juos sukūrė (ne pagal priskirtą vartotoją)."))

(def patient-deactivations-page
  (make-statistics-page patient-deactivations-by-user-in-period-table
                        "Pacientų deaktyvavimo statistika"
                        "Pacientų įrašai, kurie nėra pažymėti kaip klaidingi ir kurių deaktyvavimo data patenka į pasirinktą laikotarpį. Pacientai grupuojami pagal vartotoją, kuris juos deaktyvavo (ne pagal priskirtą vartotoją). Jei pacientas buvo deaktyvuotas kelis kartus, naudojama tik paskutinė deaktyvavimo data ir ją atlikęs vartotojas."))